/**
 * @fileoverview Full page for VAT rules management.
 * @module features/payments/pages/VatRulesPage
 */

import { useState, useEffect, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ScrollReveal } from '@/shared/components/ui'

import rulesApi from '@/features/classification-rules/api/classificationRules'
import RuleVatPopover from '@/features/classification-rules/components/RuleVatPopover'
import RuleCostLinkPopover from '@/features/classification-rules/components/RuleCostLinkPopover'
import type {
  ClassificationRuleResponse,
  ClassificationRuleRequest,
  CostLinkingRequirement,
} from '@/features/classification-rules/schemas/classificationRules'

/**
 * Full page component for managing VAT classification rules.
 */
export function VatRulesPage(): ReactElement {
  const { t } = useTranslation('features-payments')
  const navigate = useNavigate()

  const [rules, setRules] = useState<ClassificationRuleResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ClassificationRuleRequest | null>(null)

  // Load rules on mount
  useEffect(() => {
    let cancelled = false
    const load = async (): Promise<void> => {
      setLoading(true)
      try {
        const data = await rulesApi.listRules()
        if (!cancelled) setRules(data)
      } catch (e) {
        const msg = e instanceof Error ? e.message : t('vatRulesPage.toasts.loadError')
        if (!cancelled) toast.error(msg)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [t])

  // Esc to go back
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        navigate(-1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  const updateRule = async (
    r: ClassificationRuleResponse,
    patch: Partial<ClassificationRuleResponse>
  ): Promise<void> => {
    try {
      await rulesApi.updateRule(r.id, {
        vatApplicable: patch.vatApplicable ?? undefined,
        reverseCharge: patch.reverseCharge ?? undefined,
        vatRatePercent: typeof patch.vatRatePercent === 'number' ? patch.vatRatePercent : undefined,
        costLinkingRequirement: patch.costLinkingRequirement ?? undefined,
      })
      toast.success(t('vatRulesPage.toasts.ruleUpdated'))
      setRules(prev => prev.map(x => (x.id === r.id ? { ...x, ...patch } : x)))
    } catch {
      toast.error(t('vatRulesPage.toasts.updateFailed'))
    }
  }

  const removeRule = async (r: ClassificationRuleResponse): Promise<void> => {
    try {
      await rulesApi.deleteRule(r.id)
      toast.success(t('vatRulesPage.toasts.ruleDeleted'))
      setRules(prev => prev.filter(x => x.id !== r.id))
    } catch {
      toast.error(t('vatRulesPage.toasts.deleteFailed'))
    }
  }

  const beginCreate = (): void => {
    setCreating(true)
    setDraft({ priority: 0, active: true })
  }

  const cancelCreate = (): void => {
    setCreating(false)
    setDraft(null)
  }

  const saveCreate = async (): Promise<void> => {
    if (!draft) return
    const payload: ClassificationRuleRequest = {}
    if (draft.descriptionPattern && draft.descriptionPattern.trim() !== '')
      payload.descriptionPattern = draft.descriptionPattern.trim()
    if (draft.counterpartyNamePattern && draft.counterpartyNamePattern.trim() !== '')
      payload.counterpartyNamePattern = draft.counterpartyNamePattern.trim()
    if (draft.categoryUuid) payload.categoryUuid = draft.categoryUuid
    if (typeof draft.vatApplicable === 'boolean') payload.vatApplicable = draft.vatApplicable
    if (typeof draft.reverseCharge === 'boolean') payload.reverseCharge = draft.reverseCharge
    if (draft.vatApplicable === false) {
      payload.reverseCharge = false
      payload.vatRatePercent = null as unknown as number
    } else if (draft.reverseCharge === true) {
      payload.vatApplicable = true
      payload.vatRatePercent = 0
    } else if (typeof draft.vatRatePercent === 'number') {
      payload.vatRatePercent = draft.vatRatePercent
    }
    if (typeof draft.priority === 'number') payload.priority = draft.priority
    if (typeof draft.active === 'boolean') payload.active = draft.active
    if (draft.costLinkingRequirement) payload.costLinkingRequirement = draft.costLinkingRequirement

    try {
      await rulesApi.createRule(payload)
      toast.success(t('vatRulesPage.toasts.ruleCreated'))
      setCreating(false)
      setDraft(null)
      setLoading(true)
      const data = await rulesApi.listRules()
      setRules(data)
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('vatRulesPage.toasts.createFailed')
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const buildDraftRule = (): ClassificationRuleResponse => ({
    id: -1,
    uuid: 'draft',
    orgId: 'draft',
    descriptionPattern: (draft?.descriptionPattern as string | undefined) ?? null,
    counterpartyNamePattern: (draft?.counterpartyNamePattern as string | undefined) ?? null,
    categoryUuid: (draft?.categoryUuid as string | undefined) ?? null,
    vatApplicable: (draft?.vatApplicable as boolean | undefined) ?? null,
    reverseCharge: (draft?.reverseCharge as boolean | undefined) ?? null,
    vatRatePercent: (draft?.vatRatePercent as number | undefined) ?? null,
    priority: (draft?.priority as number | undefined) ?? 0,
    active: (draft?.active as boolean | undefined) ?? true,
    costLinkingRequirement: (draft?.costLinkingRequirement as CostLinkingRequirement | undefined) ?? null,
    createdAt: null,
    updatedAt: null,
  })

  if (loading && rules.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 pt-12 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="h-5 w-5" />
                <Kbd>Esc</Kbd>
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  {t('vatRulesPage.title')}
                </h1>
                <p className="text-muted-foreground">
                  {t('vatRulesPage.subtitle')}
                </p>
              </div>
            </div>
            <Button onClick={beginCreate} disabled={creating} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('vatRulesPage.addRule')}
            </Button>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <section className="rounded-3xl border border-border/15 bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-16 pl-6">{t('vatRulesPage.columns.number')}</TableHead>
                    <TableHead className="min-w-[200px]">{t('vatRulesPage.columns.title')}</TableHead>
                    <TableHead className="min-w-[200px]">{t('vatRulesPage.columns.counterparty')}</TableHead>
                    <TableHead className="w-56">{t('vatRulesPage.columns.costLinking')}</TableHead>
                    <TableHead className="w-48">{t('vatRulesPage.columns.vat')}</TableHead>
                    <TableHead className="w-32 text-right pr-6">{t('vatRulesPage.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creating ? (
                    <TableRow className="bg-secondary/50">
                      <TableCell className="text-muted-foreground pl-6">
                        <span className="text-primary font-medium">+</span>
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder={t('vatRulesPage.placeholders.titlePattern')}
                          value={draft?.descriptionPattern ?? ''}
                          onChange={e =>
                            setDraft(d => ({ ...(d ?? {}), descriptionPattern: e.target.value }))
                          }
                          className="bg-background"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder={t('vatRulesPage.placeholders.counterpartyPattern')}
                          value={draft?.counterpartyNamePattern ?? ''}
                          onChange={e =>
                            setDraft(d => ({ ...(d ?? {}), counterpartyNamePattern: e.target.value }))
                          }
                          className="bg-background"
                        />
                      </TableCell>
                      <TableCell>
                        <RuleCostLinkPopover
                          rule={buildDraftRule()}
                          onApply={async p => setDraft(d => ({ ...(d ?? {}), ...p }))}
                        />
                      </TableCell>
                      <TableCell>
                        <RuleVatPopover
                          rule={buildDraftRule()}
                          onApply={async p => setDraft(d => ({ ...(d ?? {}), ...p }))}
                        />
                      </TableCell>
                      <TableCell className="pr-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void saveCreate()}
                          >
                            {t('vatRulesPage.save')}
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={cancelCreate}>
                            {t('vatRulesPage.cancel')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-16 text-center text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        {t('vatRulesPage.states.loading')}
                      </TableCell>
                    </TableRow>
                  ) : rules.length === 0 && !creating ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-16 text-center text-muted-foreground">
                        <p className="mb-4">{t('vatRulesPage.states.empty')}</p>
                        <Button variant="outline" onClick={beginCreate} className="gap-2">
                          <Plus className="h-4 w-4" />
                          {t('vatRulesPage.addFirstRule')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rules.map((r, idx) => (
                      <TableRow key={r.id} className="group">
                        <TableCell className="text-muted-foreground pl-6 font-mono text-sm">
                          {idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {r.descriptionPattern ?? <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {r.counterpartyNamePattern ?? <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          <RuleCostLinkPopover
                            rule={r}
                            onApply={async patch => {
                              await updateRule(r, patch)
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <RuleVatPopover
                            rule={r}
                            onApply={async patch => {
                              await updateRule(r, patch)
                            }}
                          />
                        </TableCell>
                        <TableCell className="pr-6">
                          <div className="flex items-center justify-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => void removeRule(r)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-2xl p-6">
            <h3 className="font-medium text-foreground mb-2">
              {t('vatRulesPage.help.title')}
            </h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>{t('vatRulesPage.help.pattern')}</li>
              <li>{t('vatRulesPage.help.priority')}</li>
              <li>{t('vatRulesPage.help.apply')}</li>
            </ul>
          </div>
        </ScrollReveal>
      </div>
    </div>
  )
}
